import React from "react";
import { renderToStaticMarkup } from "react-dom/server"; // optional, see note below

/**
 * Convert an "icon spec" ( { viewBox, path, fill?, stroke? } ) to an <svg> string.
 * This does NOT require ReactDOMServer unless your `path` is JSX.
 */
export function iconToSvgString(
  iconSpec,
  { size = 24, color = "currentColor", strokeWidth = 2, title, className } = {}
) {
  if (!iconSpec || !iconSpec.path) return "";

  const {
    viewBox = "0 0 24 24",
    path,
    fill = "none",
    stroke = color,
  } = iconSpec;

  // If `path` is a React node (JSX), we need renderToStaticMarkup to stringify it.
  // If your `path` is plain string paths, skip ReactDOMServer and build manually.
  const needsReactRender = React.isValidElement(path);

  if (!needsReactRender && typeof path === "string") {
    // Plain string path(s)
    return `
<svg xmlns="http://www.w3.org/2000/svg"
     width="${size}" height="${size}"
     viewBox="${viewBox}"
     fill="${fill}" stroke="${stroke}"
     stroke-width="${strokeWidth}"
     stroke-linecap="round" stroke-linejoin="round"
     ${className ? `class="${className}"` : ""}
     role="img" ${title ? `aria-label="${title}"` : `aria-hidden="true"`}>
  ${title ? `<title>${escapeHtml(title)}</title>` : ""}
  ${path}
</svg>`.trim();
  }

  // Fallback: render the full <svg> with JSX path via ReactDOMServer
  const svgEl = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={viewBox}
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role="img"
      {...(title ? { "aria-label": title } : { "aria-hidden": true })}
    >
      {title ? <title>{title}</title> : null}
      {path}
    </svg>
  );

  return renderToStaticMarkup(svgEl);
}

// createIconNode.js
const SVG_NS = "http://www.w3.org/2000/svg";

/**
 *
 * @param {*} spec data spec, not jsx spec
 * @param {*} param1
 * @returns
 */
export function iconToSvgElement(
  spec,
  {
    size = 24,
    color = "currentColor",
    strokeWidth = 2,
    className = "",
    title = undefined,
    attrs = {}, // extra attributes if needed
  } = {}
) {
  const { viewBox = "0 0 24 24", children = [] } = spec || {};
  const svg = document.createElementNS(SVG_NS, "svg");

  svg.setAttribute("xmlns", SVG_NS);
  svg.setAttribute("viewBox", viewBox);
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("fill", color);
  svg.setAttribute("stroke", color);
  svg.setAttribute("stroke-width", String(strokeWidth));
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  if (className) svg.setAttribute("class", className);
  if (title) {
    const t = document.createElementNS(SVG_NS, "title");
    t.textContent = title;
    svg.appendChild(t);
  }
  // extra attributes
  Object.entries(attrs).forEach(([k, v]) => svg.setAttribute(k, String(v)));

  // build children
  for (const node of children) {
    const el = document.createElementNS(SVG_NS, node.tag);
    for (const [k, v] of Object.entries(node.attrs || {})) {
      el.setAttribute(k, String(v));
    }
    svg.appendChild(el);
  }

  return svg;
}

function escapeHtml(s) {
  return String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

/**
 * Convert an existing "icon spec" that uses JSX in `path` into a
 * data-spec object with { viewBox, children: [{tag, attrs, children}] }.
 *
 * Example:
 *   const dataSpec = convertIconSpecToData(IconMap.airplane)
 */
export function convertIconSpecToData({
  iconSpec,
  size = 24,
  className = "",
  color = "currentColor",
  strokeWidth = 2,
  title,
  ...rest
}) {
  // console.log(iconSpec);

  const {
    viewBox = "0 0 24 24",
    path,
    fill = color,
    stroke = color,
  } = /** @type {IconSpec|undefined} */ (iconSpec) || {};

  if (!iconSpec || !path) {
    // Return type: null  (no valid content to render)
    return null;
  }

  // if (!iconSpec) return null;

  // const { viewBox = "0 0 24 24", path } = iconSpec;
  const children = flattenToArray(jsxToData(path)).filter(Boolean);

  return { size, color, title, viewBox, children };
}

/* ------------------------- JSX -> data core -------------------------------- */

/**
 * Recursively convert a React node (JSX) into a data node:
 * - React elements -> { tag, attrs, children }
 * - Fragments -> unwrap children
 * - Text -> string
 * - Arrays -> array of data nodes
 * - null/boolean -> null
 */
function jsxToData(node) {
  // null/undefined/boolean: ignore
  if (node == null || typeof node === "boolean") return null;

  // Text node
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }

  // Arrays: map/flatten
  if (Array.isArray(node)) {
    return node.flatMap(jsxToData);
  }

  // React element?
  if (React.isValidElement(node)) {
    const { type, props } = node;

    // React.Fragment -> unwrap its children only
    if (type === React.Fragment) {
      return jsxToData(props?.children);
    }

    // If `type` is a string, it's a real SVG/HTML tag (e.g. "path", "g", "svg", etc.)
    if (typeof type === "string") {
      const { children, ...rest } = props || {};
      const attrs = propsToAttrs(rest);
      const dataChildren = flattenToArray(jsxToData(children)).filter(Boolean);

      const dataNode = { tag: type };
      if (Object.keys(attrs).length) dataNode.attrs = attrs;
      if (dataChildren.length) dataNode.children = dataChildren;
      return dataNode;
    }

    // If `type` is a component/function, we can't safely execute it here.
    // Best effort: treat it like a leaf "g" wrapper with a hint name.
    // (You can customize this based on your icon library usage.)
    return { tag: "g", attrs: { "data-component": getDisplayName(type) } };
  }

  // Fallback: stringify anything else
  try {
    return String(node);
  } catch {
    return null;
  }
}

/* ------------------------- utils ------------------------------------------- */

function flattenToArray(v) {
  return Array.isArray(v) ? v : v == null ? [] : [v];
}

// Convert React props into SVG attributes
function propsToAttrs(props) {
  const out = {};
  for (const [key, val] of Object.entries(props || {})) {
    if (key === "children" || val == null) continue;

    // className -> class
    if (key === "className") {
      out["class"] = String(val);
      continue;
    }

    // style object -> style string
    if (key === "style" && val && typeof val === "object") {
      out["style"] = styleObjToString(val);
      continue;
    }

    // data-* / aria-* keep as-is
    if (key.startsWith("data-") || key.startsWith("aria-")) {
      out[key] = String(val);
      continue;
    }

    // Convert camelCase to kebab-case for SVG attributes (strokeWidth -> stroke-width)
    const attrName = camelToKebab(key);

    // Booleans: presence means true on SVG; false -> skip
    if (typeof val === "boolean") {
      if (val) out[attrName] = "true";
      continue;
    }

    out[attrName] = String(val);
  }
  return out;
}

function camelToKebab(s) {
  return s.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase());
}

function styleObjToString(obj) {
  return Object.entries(obj)
    .map(([k, v]) => `${camelToKebab(k)}:${v}`)
    .join(";");
}

function getDisplayName(fn) {
  return fn.displayName || fn.name || "Anonymous";
}
